import { isNil } from 'lodash';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

// HOCs
import withFormikInput from './withFormikInput';

// COMPONENTS
import ConnectedField from './connectedField';
import { NumberField } from './numberField';
import { ReportedOdometerTag, CalculatedOdometerTag } from 'components/Odometer';

// CONSTANTS
import { ODOMETER_STRATEGY } from 'constants/common';

// SELECTORs
import { selectFleetSettings } from 'services/FleetSettings/selectors';

// UTILS
import { isNilOrEmpty } from 'utils';

const config = {
  getValidity: ({
    connectedOdometer,
    connectedOdometerDate,
    reportedOdometer,
    reportedOdometerDate,
    odometerInitial,
    odometerInitialDate,
    odometerType,
  }) =>
    (odometerType === ODOMETER_STRATEGY.reported && !isNil(reportedOdometer) && !isNil(reportedOdometerDate)) ||
    (!isNil(connectedOdometer) &&
      !isNil(connectedOdometerDate) &&
      !isNil(odometerInitial) &&
      !isNil(odometerInitialDate)),
  getConnectivityStatusText: (meta) => {
    if (meta.odometerType === ODOMETER_STRATEGY.calculated) {
      if (isNil(meta.odometerInitialDate)) {
        return {
          translationId: 'services.Vehicles.vehicleInitiallyConnected',
        };
      }

      if (isNil(meta.odometerInitial)) {
        return { translationId: 'services.Vehicles.vehicleReconnected' };
      }
    }

    if (isNil(meta.connectedOdometer) && isNil(meta.reportedOdometer)) {
      return { translationId: 'services.Vehicles.noDataReceivedYet' };
    }

    // return { translationId: 'services.Vehicles.odometerNotAvailable' };
    return null;
  },
};

const ReportedOdometerTagWithMargin = styled(ReportedOdometerTag)`
  position: absolute;
  right: 2px;
`;

const CalculatedOdometerTagWithMargin = styled(CalculatedOdometerTag)`
  position: absolute;
  right: 2px;
`;

export const _ConnectedOdometerField = (props) => {
  const {
    connectedMetadata,
    disabled,
    edited,
    error,
    label,
    name,
    onClickRefresh,
    readOnly,
    required,
    setEdited,
    touched,
    value,
    validators,
    setAutofillEditedValues,
    ...rest
  } = props;

  const fleetSettings = useSelector(selectFleetSettings());

  const onChange = (event) => {
    if (event.persist) {
      event.persist();
    }
    const { value: eventValue } = event.target;

    if (eventValue !== props.value || !props.edited) {
      props.onChange(event);
    }
    if (!props.edited) {
      props.setEdited(props.name, true);
    }
    if (props.setAutofillEditedValues) {
      props.setAutofillEditedValues(props.name, eventValue);
    }
  };

  const handleRefresh = () => {
    props.onClickRefresh();
  };

  let connectedOdometer = connectedMetadata.isConnected ? connectedMetadata.connectedOdometer : '';

  let { connectedOdometerDate } = connectedMetadata;

  let odometerType = ODOMETER_STRATEGY.calculated;

  if (
    fleetSettings.get('preferredOdometerStrategy') === ODOMETER_STRATEGY.reported &&
    !isNil(connectedMetadata.reportedOdometer)
  ) {
    connectedOdometer = connectedMetadata.reportedOdometer;
    connectedOdometerDate = connectedMetadata.reportedOdometerDate;
    odometerType = ODOMETER_STRATEGY.reported;
  }

  const resolvedValue = !isNilOrEmpty(value) || edited ? value : connectedOdometer;

  const fieldProps = {
    ...rest,
    disabled,
    name,
    readOnly,
    onChange,
    value: resolvedValue,
  };

  const isValid = config.getValidity({ ...connectedMetadata, odometerType });

  const connectedFieldProps = {
    name,
    disabled,
    label,
    readOnly,
    error,
    hasValue: !isNilOrEmpty(resolvedValue),
    isConnected: connectedMetadata.isConnected,
    isManuallyEdited: !isNilOrEmpty(value) || edited,
    isValid,
    lastConnectedDate: connectedOdometerDate,
    onClickRefresh: handleRefresh,
    statusText: config.getConnectivityStatusText({
      ...connectedMetadata,
      odometerType,
    }),
  };

  return (
    <ConnectedField {...connectedFieldProps}>
      <NumberField {...fieldProps} />
      {connectedMetadata.isConnected &&
        isValid &&
        !connectedFieldProps.isManuallyEdited &&
        (fleetSettings.get('preferredOdometerStrategy') === ODOMETER_STRATEGY.reported &&
        !isNil(connectedMetadata.reportedOdometer) ? (
          <ReportedOdometerTagWithMargin showTooltip showInfoLink />
        ) : (
          <CalculatedOdometerTagWithMargin showTooltip showInfoLink />
        ))}
    </ConnectedField>
  );
};

const FormConnectedOdometerField = withFormikInput(_ConnectedOdometerField, {
  passProps: ['touched'],
  watchedProps: ['edited', 'connectedMetadata'],
});

export default FormConnectedOdometerField;
